import React, { useEffect, useRef, useState } from 'react';
import { Modal, ConfirmModal } from './ui/Modal';
import { Search, FileCode, ArrowLeft, Trash2 } from 'lucide-react';
import { useTabsStore, setDbQueryPickerOpen, openDbManagerQueryTab } from '../store/tabsStore';
import {
  isDbConnectionLive,
  listDbCapableExtensionIds,
  listSavedDbConnections,
  connectDbFromSavedConfig,
  readSavedDbConnection,
  DB_PASSWORD_REQUIRED,
  markDbConnectionClosed,
} from '../extensions/capabilities/db';
import { listDatabases, currentScriptScope, DB_QUERY_COMMANDS, DEFAULT_DB_PORTS, DB_MANAGER_EXTENSION_ID } from '../extensions/dbEngines';
import { invokeAsExtension } from '../extensions/capabilities/enforce';
import { DbBrandIcon } from '../extensions/dbBrandIcons';
import DbPasswordPromptModal from './DbPasswordPromptModal';
import { setDbConnectionPassword } from '../extensions/capabilities/dbVaultConnections';
import { dbScriptTargetLabel } from '../utils/dbScriptLabel';

/** Display label for a saved connection (shared by conn and db rows). */
function connHostLabel(conn, engine) {
  return (
    conn.name ||
    (engine === 'sqlite'
      ? String(conn.path || 'SQLite').split(/[\\/]/).pop()
      : `${conn.user || ''}@${conn.host || 'localhost'}:${conn.port || DEFAULT_DB_PORTS[engine]}`)
  );
}

/**
 * "+" in the DB Manager tab strip opens this picker. Root view: saved scripts
 * (scripts/db/*.sql) and a "new query" list, both filterable. Live
 * connections show their databases directly; saved-but-not-live connections
 * show as a single row that connects on click. The picker NEVER dials a
 * server on its own: auto-dialing every saved connection reconnected servers
 * the user deliberately disconnected and let one dead host blank the whole
 * list for the full connect timeout. Picking a database opens an empty query
 * tab; picking a script first asks which database to run it against (step 2),
 * then opens the tab preloaded with the script's content + name.
 */
export default function DbQueryTabPickerModal() {
  const open = useTabsStore((s) => s.dbQueryPickerOpen);
  const [filter, setFilter] = useState('');
  // Heterogeneous rows: { kind: 'db', extensionId, connId, engine, hostLabel,
  // database } opens a tab; { kind: 'conn', …, status: 'idle' | 'listing' |
  // 'connecting' | 'error', error? } connects + expands in place on click.
  const [entries, setEntries] = useState(null); // null = loading
  const [scripts, setScripts] = useState([]);
  // Step 2: script chosen, waiting for the target database.
  const [pendingScript, setPendingScript] = useState(null); // { name, content } | null
  // connId → the connection's own database: PostgreSQL scripts are labelled by it (dbScriptLabel).
  const [connDatabases, setConnDatabases] = useState({});
  // A connection waiting on a password, plus the resolver its prompt answers.
  const [passwordPrompt, setPasswordPrompt] = useState(null);
  // Script pending delete confirmation. window.confirm is a Tauri dialog-plugin
  // command this app's capabilities do not grant, so it throws rather than
  // returning false — see DbManagerResultsTab for the same in-app substitute.
  const [confirmDeleteScript, setConfirmDeleteScript] = useState(null);

  /**
   * Show the prompt and connect with what was typed.
   *
   * Returns the engine on success, or null if the prompt was cancelled or the
   * password was wrong — either way the caller leaves the row alone rather
   * than painting an error the user already saw.
   */
  const promptForPassword = ({ extensionId, connId, hostLabel }) =>
    new Promise((resolve) => {
      setPasswordPrompt({
        extensionId,
        connId,
        hostLabel,
        resolve: async ({ password, remember }) => {
          setPasswordPrompt(null);
          try {
            const engine = await connectDbFromSavedConfig(extensionId, connId, { password });
            if (remember && engine) {
              // Into the vault item, not the OS credential store — one place
              // holds the connection. Only after a connect that worked:
              // storing a rejected password would make every later attempt
              // fail silently with it.
              //
              // And only for a VAULT connection, which is the same guard the
              // results tab applies. setDbConnectionPassword rejects anything
              // else with not_a_db_connection, and the catch below would have
              // swallowed it — so ticking "remember" on a legacy or
              // third-party connection did nothing at all, silently, and the
              // user was prompted again next time with no clue why.
              const saved = await readSavedDbConnection(extensionId, connId).catch(() => null);
              if (saved?.origin === 'vault') {
                await setDbConnectionPassword(connId, password).catch(() => {});
              }
            }
            resolve({ engine });
          } catch {
            resolve(null);
          }
        },
        cancel: () => {
          setPasswordPrompt(null);
          resolve(null);
        },
      });
    });
  // Bumped on every open; in-flight connects/listings from a previous open
  // check it before merging so they can't pollute a fresh list.
  const genRef = useRef(0);

  // Replace a connection's rows (its conn placeholder or previous databases)
  // with fresh ones — merges are per-connection so one slow server never
  // blocks the rest of the list.
  const replaceConnRows = (gen, extensionId, connId, rows) => {
    if (genRef.current !== gen) return;
    setEntries((prev) => {
      const rest = (prev || []).filter((e) => !(e.extensionId === extensionId && e.connId === connId));
      return [...rest, ...rows];
    });
  };

  const expandConnection = async (gen, { extensionId, connId, engine, hostLabel }, { dial }) => {
    try {
      if (dial && !isDbConnectionLive(extensionId, connId)) {
        let connected;
        try {
          connected = await connectDbFromSavedConfig(extensionId, connId);
        } catch (err) {
          // No stored password. Ask for it rather than reporting whatever the
          // server says about an empty one, which reads as the server
          // rejecting credentials the app never sent.
          if (String(err?.message) !== DB_PASSWORD_REQUIRED) throw err;
          const supplied = await promptForPassword({ extensionId, connId, hostLabel });
          if (!supplied) return; // cancelled — leave the row as it was
          connected = supplied.engine;
        }
        if (!connected) {
          throw new Error('Missing saved credentials — connect from the DB Manager panel first.');
        }
      }
      const databases = await listDatabases(engine, extensionId, connId);
      replaceConnRows(
        gen,
        extensionId,
        connId,
        databases.map((database) => ({ kind: 'db', extensionId, connId, engine, hostLabel, database }))
      );
    } catch (e) {
      // A supposedly-live connection whose listing failed is dead — record
      // that so the rest of the app stops treating it as live.
      if (!dial) markDbConnectionClosed(extensionId, connId);
      replaceConnRows(gen, extensionId, connId, [
        {
          kind: 'conn',
          extensionId,
          connId,
          engine,
          hostLabel,
          status: 'error',
          error: String(e?.message || e || 'Connection failed'),
        },
      ]);
    }
  };

  // User clicked a saved-but-not-live connection row: this is the only place
  // the picker ever dials, and only for the row the user chose.
  const connectRow = (entry) => {
    const gen = genRef.current;
    setEntries((prev) =>
      (prev || []).map((e) =>
        e.kind === 'conn' && e.extensionId === entry.extensionId && e.connId === entry.connId
          ? { ...e, status: 'connecting', error: null }
          : e
      )
    );
    expandConnection(gen, entry, { dial: true });
  };

  useEffect(() => {
    if (!open) return undefined;
    setFilter('');
    setEntries(null);
    setScripts([]);
    setPendingScript(null);
    genRef.current += 1;
    const gen = genRef.current;
    let alive = true;
    invokeAsExtension('db_scripts_list', { extensionId: DB_MANAGER_EXTENSION_ID, scope: currentScriptScope() })
      .then((list) => {
        if (alive) setScripts(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    (async () => {
      // Saved connections are a fast local read — render them immediately,
      // one row per connection, without dialing anything.
      const savedDatabases = [];
      const perExtension = await Promise.all(
        listDbCapableExtensionIds().map(async (extensionId) => {
          const saved = await listSavedDbConnections(extensionId).catch(() => []);
          saved.forEach((conn) => conn?.id && conn.database && savedDatabases.push([conn.id, conn.database]));
          return saved
            .filter((conn) => conn && typeof conn === 'object' && conn.id)
            .map((conn) => {
              const engine = DB_QUERY_COMMANDS[conn.engine] ? conn.engine : 'mysql';
              return {
                kind: 'conn',
                extensionId,
                connId: conn.id,
                engine,
                hostLabel: connHostLabel(conn, engine),
                status: isDbConnectionLive(extensionId, conn.id) ? 'listing' : 'idle',
              };
            });
        })
      );
      if (!alive || genRef.current !== gen) return;
      setConnDatabases(Object.fromEntries(savedDatabases));
      const all = perExtension.flat();
      setEntries(all);
      // Already-live connections expand into database rows as each listing
      // resolves (listDatabases runs a query — live pools only, no dialing).
      for (const entry of all) {
        if (entry.status === 'listing') expandConnection(gen, entry, { dial: false });
      }
    })();
    return () => {
      alive = false;
    };
    // expandConnection is deliberately not a dep: it's recreated per render
    // and the effect must run only when the modal opens (gen guards staleness).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const close = () => setDbQueryPickerOpen(false);
  const query = filter.trim().toLowerCase();
  // Stable grouping while connections expand asynchronously: rows sort by
  // host label, a connection's databases replacing its placeholder in place.
  const filteredDbs = (entries || [])
    .filter((e) => !query || `${e.hostLabel} ${e.database || ''}`.toLowerCase().includes(query))
    .sort(
      (a, b) =>
        String(a.hostLabel).localeCompare(String(b.hostLabel)) ||
        String(a.database || '').localeCompare(String(b.database || ''))
    );
  const filteredScripts = scripts.filter(
    (s) => !query || `${s.name} ${s.meta?.dbName || ''}`.toLowerCase().includes(query)
  );

  const openTab = (entry, script) => {
    openDbManagerQueryTab({
      extensionId: entry.extensionId,
      connId: entry.connId,
      engine: entry.engine,
      dbName: entry.engine === 'sqlite' ? null : entry.database,
      sql: script?.content ?? '',
      scriptName: script?.name ?? null,
      // Sidecar-less scripts stay unlinked: a later Ctrl+S keeps them
      // database-agnostic instead of silently binding them.
      scriptLinked: script ? script.linked !== false : true,
    });
    close();
  };

  const deleteScript = async (script) => {
    try {
      await invokeAsExtension('db_script_delete', { extensionId: DB_MANAGER_EXTENSION_ID, name: script.name, scope: currentScriptScope() });
      setScripts((prev) => prev.filter((s) => s.name !== script.name));
    } catch {
      // File already gone — refresh to resync.
      invokeAsExtension('db_scripts_list', { extensionId: DB_MANAGER_EXTENSION_ID, scope: currentScriptScope() })
        .then((list) => setScripts(Array.isArray(list) ? list : []))
        .catch(() => {});
    }
  };

  const pickScript = async (script) => {
    try {
      const content = await invokeAsExtension('db_script_read', { extensionId: DB_MANAGER_EXTENSION_ID, name: script.name, scope: currentScriptScope() });
      // The sidecar binds the script to its database — when that exact
      // target is live, open directly; otherwise fall back to the chooser
      // (connection down, database dropped, or a pre-sidecar script).
      const meta = script.meta || null;
      if (meta) {
        // Only database rows (live connections) auto-open; a conn row for the
        // target means the connection is down — fall to the chooser, where
        // the user can connect it explicitly.
        const match = (entries || []).find(
          (e) =>
            e.kind === 'db' &&
            e.extensionId === meta.extensionId &&
            e.connId === meta.connId &&
            e.engine === meta.engine &&
            (e.engine === 'sqlite' || e.database === meta.dbName)
        );
        if (match) {
          openTab(match, { name: script.name, content, linked: true });
          return;
        }
      }
      setPendingScript({ name: script.name, content, linked: !!meta });
      setFilter('');
    } catch {
      // File vanished since listing — refresh the list.
      invokeAsExtension('db_scripts_list', { extensionId: DB_MANAGER_EXTENSION_ID, scope: currentScriptScope() }).then((list) => setScripts(Array.isArray(list) ? list : [])).catch(() => {});
    }
  };

  const dbRow = (entry, script) => {
    if (entry.kind === 'conn') {
      const busy = entry.status === 'connecting' || entry.status === 'listing';
      return (
        <button
          key={`conn ${entry.extensionId} ${entry.connId}`}
          type="button"
          className="dbq-picker-row"
          onClick={() => connectRow(entry)}
          disabled={busy}
          title={entry.status === 'error' ? entry.error : undefined}
        >
          <DbBrandIcon name={entry.engine} size={18} />
          <span className="dbq-picker-db">{entry.hostLabel}</span>
          <span
            className="dbq-picker-host"
            style={entry.status === 'error' ? { color: 'var(--danger, #ef4444)' } : undefined}
          >
            {busy ? (
              <span className="dbm-spinner" aria-hidden />
            ) : entry.status === 'error' ? (
              entry.error || 'Connection failed'
            ) : (
              'Connect to list databases…'
            )}
          </span>
        </button>
      );
    }
    return (
      <button
        key={`db ${entry.extensionId} ${entry.connId} ${entry.database}`}
        type="button"
        className="dbq-picker-row"
        onClick={() => openTab(entry, script)}
      >
        <DbBrandIcon name={entry.engine} size={18} />
        <span className="dbq-picker-db">{entry.database}</span>
        <span className="dbq-picker-host">{entry.hostLabel}</span>
      </button>
    );
  };

  const dbList = (script) =>
    entries === null ? (
      <div className="dbq-picker-empty" role="status" aria-label="Loading databases">
        <span className="dbm-spinner" aria-hidden />
      </div>
    ) : entries.length === 0 ? (
      <div className="dbq-picker-empty">
        No saved database connections. Open the DB Manager panel and add one first.
      </div>
    ) : filteredDbs.length === 0 ? (
      <div className="dbq-picker-empty">No matches.</div>
    ) : (
      filteredDbs.map((entry) => dbRow(entry, script))
    );

  return (
    <Modal
      isOpen
      onClose={close}
      title="New query tab"
      subtitle={
        pendingScript
          ? `Choose the database "${pendingScript.name}" runs against`
          : 'Open a saved script, or start a new query on a database'
      }
    >
      <div className="dbq-picker-search">
        <Search size={14} className="dbq-picker-search-icon" aria-hidden />
        <input
          type="text"
          autoFocus
          className="modal-input dbq-picker-search-input"
          placeholder={pendingScript ? 'Filter databases' : 'Filter scripts and databases'}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      <div className="dbq-picker-list ext-modal-body">
        {pendingScript ? (
          <>
            <button type="button" className="dbq-picker-back" onClick={() => setPendingScript(null)}>
              <ArrowLeft size={13} aria-hidden /> All scripts
            </button>
            {dbList(pendingScript)}
          </>
        ) : (
          <>
            {filteredScripts.length > 0 && (
              <>
                <div className="dbq-picker-section">Saved scripts</div>
                {filteredScripts.map((script) => (
                  <div key={script.name} className="dbq-picker-row-wrap">
                    <button
                      type="button"
                      className="dbq-picker-row"
                      onClick={() => pickScript(script)}
                    >
                      <FileCode size={16} style={{ color: 'var(--accent)' }} aria-hidden />
                      <span className="dbq-picker-db">{script.name}</span>
                      <span className="dbq-picker-host">
                        {script.meta?.dbName
                          ? dbScriptTargetLabel(script.meta, connDatabases)
                          : script.modifiedMs > 0
                            ? new Date(script.modifiedMs).toLocaleDateString()
                            : ''}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="dbq-picker-row-delete"
                      title={`Delete "${script.name}"`}
                      aria-label={`Delete script ${script.name}`}
                      onClick={() => setConfirmDeleteScript(script)}
                    >
                      <Trash2 size={13} aria-hidden />
                    </button>
                  </div>
                ))}
              </>
            )}
            <div className="dbq-picker-section">New query</div>
            {dbList(null)}
          </>
        )}
      </div>
      {confirmDeleteScript && (
        <ConfirmModal
          isOpen
          onClose={() => setConfirmDeleteScript(null)}
          onConfirm={() => deleteScript(confirmDeleteScript)}
          title="Delete script"
          message={`Delete the saved script "${confirmDeleteScript.name}"? This cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      )}
      {passwordPrompt && (
        <DbPasswordPromptModal
          open
          connectionLabel={passwordPrompt.hostLabel}
          onSubmit={passwordPrompt.resolve}
          onClose={passwordPrompt.cancel}
        />
      )}
    </Modal>
  );
}
